import PropTypes from 'prop-types';
import { t } from '../../../../../modules/infra/i18n';

export const permissionPropType = PropTypes.shape({
  fieldName: PropTypes.string.isRequired,
  requires: PropTypes.arrayOf(PropTypes.string),
  requiredFor: PropTypes.arrayOf(PropTypes.string),
  tooltip: PropTypes.string,
});

export const permissionRowPropType = PropTypes.shape({
  rowId: PropTypes.string.isRequired,
  rowTitle: PropTypes.string.isRequired,
  hide: PropTypes.bool,
  permissions: PropTypes.shape({
    view: permissionPropType,
    addEdit: permissionPropType,
    book: permissionPropType,
    modify: permissionPropType,
    history: permissionPropType,
    cancel: permissionPropType,
    blockTime: permissionPropType,
  }).isRequired,
});

export const permissionSectionPropType = PropTypes.shape({
  sectionId: PropTypes.string.isRequired,
  sectionTitle: PropTypes.string.isRequired,
  showColumns: PropTypes.arrayOf(
    PropTypes.oneOf(['view', 'addEdit', 'book', 'modify', 'history', 'cancel', 'blockTime'])
  ),
  rows: PropTypes.arrayOf(permissionRowPropType).isRequired,
  hide: PropTypes.bool,
});

export const permissionsDataPropType = PropTypes.arrayOf(permissionPropType);

export default ({ canSeeEngagePermissions, canSeeAutoPayoutPermissions }) => [
  {
    sectionId: 'appointmentsManagement',
    sectionTitle: t('barber:staff.permissions.sections.appointments_management'),
    showColumns: ['view', 'addEdit', 'book', 'modify', 'history', 'cancel', 'blockTime'],
    rows: [
      {
        rowId: 'appointments',
        rowTitle: t('barber:staff.permissions.rows.appointments'),
        permissions: {
          view: { fieldName: 'permissions.appointments.view' },
          book: { fieldName: 'permissions.appointments.book' },
          modify: { fieldName: 'permissions.appointments.modify' },
          history: { fieldName: 'permissions.appointments.history' },
          cancel: { fieldName: 'permissions.appointments.cancel' },
          blockTime: { fieldName: 'permissions.blockTime.modify' },
        },
      },
      {
        rowId: 'appointmentsViewContactInfo',
        rowTitle: t('barber:staff.permissions.rows.form_show_contact_info'),
        permissions: {
          view: { fieldName: 'permissions.appointments.show_contact_info' },
        },
      },
    ],
  },
  {
    sectionId: 'clientManagement',
    sectionTitle: t('barber:staff.permissions.sections.client_management'),
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'clients',
        rowTitle: t('barber:staff.permissions.rows.clients'),
        permissions: {
          view: { fieldName: 'permissions.clients.view' },
          addEdit: { fieldName: 'permissions.clients.modify' },
        },
      },
    ],
  },
  {
    sectionId: 'staffManagement',
    sectionTitle: t('barber:staff.permissions.sections.staff_management'),
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'staff',
        rowTitle: t('barber:staff.permissions.rows.staff'),
        permissions: {
          view: {
            fieldName: 'permissions.staffManagement.view',
            // eslint-disable-next-line max-len
            tooltip: t('barber:staff.permissions.tooltips.staff_management_view'),
          },
          addEdit: {
            fieldName: 'permissions.staffManagement.modify',
            // eslint-disable-next-line max-len
            tooltip: t('barber:staff.permissions.tooltips.staff_management_add_edit'),
          },
        },
      },
      {
        rowId: 'barbers',
        rowTitle: t('barber:staff.permissions.rows.barbers'),
        permissions: {
          view: { fieldName: 'permissions.barbers.view' },
          addEdit: { fieldName: 'permissions.barbers.modify' },
        },
      },
      {
        rowId: 'barbersProfile',
        rowTitle: t('barber:staff.permissions.rows.barbers_profile'),
        permissions: {
          view: { fieldName: 'permissions.profile.view' },
          addEdit: { fieldName: 'permissions.profile.modify' },
        },
      },
      {
        rowId: 'barbersSettings',
        rowTitle: t('barber:staff.permissions.rows.barbers_settings'),
        permissions: {
          view: { fieldName: 'permissions.barberSettings.view' },
          addEdit: { fieldName: 'permissions.barberSettings.modify' },
        },
      },
    ],
  },
  {
    sectionId: 'servicesManagement',
    sectionTitle: t('barber:staff.permissions.sections.services_management'),
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'services',
        rowTitle: t('barber:staff.permissions.rows.services'),
        permissions: {
          view: { fieldName: 'permissions.services.view' },
          addEdit: { fieldName: 'permissions.services.modify' },
        },
      },
    ],
  },
  {
    sectionId: 'paymentSettings',
    sectionTitle: t('barber:staff.permissions.sections.payment_settings'),
    showColumns: ['view', 'addEdit'],
    hide: !canSeeAutoPayoutPermissions,
    rows: [
      {
        rowId: 'autoPayout',
        rowTitle: t('barber:staff.permissions.rows.auto_payout'),
        permissions: {
          view: {
            fieldName: 'permissions.autoPayout.view',
            requiredFor: ['permissions.autoPayout.modify'],
            tooltip: t('barber:staff.permissions.tooltips.auto_payout_view'),
          },
          addEdit: {
            fieldName: 'permissions.autoPayout.modify',
            requires: ['permissions.autoPayout.view'],
            tooltip: t('barber:staff.permissions.tooltips.auto_payout_add_edit'),
          },
        },
      },
    ],
  },
  {
    sectionId: 'productsManagement',
    sectionTitle: t('barber:staff.permissions.sections.products_management'),
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'products',
        rowTitle: t('barber:staff.permissions.rows.products'),
        permissions: {
          view: {
            fieldName: 'permissions.products.view',
            requiredFor: ['permissions.products.modify', 'permissions.inventory.modify'],
          },
          addEdit: {
            fieldName: 'permissions.products.modify',
            requires: ['permissions.products.view'],
          },
        },
      },
      {
        rowId: 'inventory',
        rowTitle: t('barber:staff.permissions.rows.inventory'),
        permissions: {
          addEdit: {
            fieldName: 'permissions.inventory.modify',
            requires: ['permissions.products.view'],
            // eslint-disable-next-line max-len
            tooltip: t('barber:staff.permissions.tooltips.inventory_add_edit'),
          },
        },
      },
    ],
  },
  {
    sectionId: 'shopSettings',
    sectionTitle: t('barber:staff.permissions.sections.shop_settings'),
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'shopDetails',
        rowTitle: t('barber:staff.permissions.rows.shop_details'),
        permissions: {
          view: { fieldName: 'permissions.settings.view' },
          addEdit: { fieldName: 'permissions.settings.modify' },
        },
      },
      {
        rowId: 'bankAccount',
        rowTitle: t('barber:staff.permissions.rows.bank_account'),
        permissions: {
          view: { fieldName: 'permissions.bankAccount.view' },
          addEdit: { fieldName: 'permissions.bankAccount.modify' },
        },
      },
      {
        rowId: 'taxes',
        rowTitle: t('barber:staff.permissions.rows.taxes'),
        permissions: {
          view: { fieldName: 'permissions.taxes.view' },
          addEdit: { fieldName: 'permissions.taxes.modify' },
        },
      },
      {
        rowId: 'waitingList',
        rowTitle: t('barber:staff.permissions.rows.waiting_list'),
        permissions: {
          view: { fieldName: 'permissions.waitingList.view' },
          addEdit: { fieldName: 'permissions.waitingList.modify' },
        },
      },
    ],
  },
  {
    sectionId: 'reportingManagement',
    sectionTitle: t('barber:staff.permissions.sections.reporting_management'),
    showColumns: ['view'],
    rows: [
      {
        rowId: 'reports',
        rowTitle: t('barber:staff.permissions.rows.reports'),
        permissions: {
          view: { fieldName: 'permissions.reports.view' },
        },
      },
      {
        rowId: 'timeReports',
        rowTitle: t('barber:staff.permissions.rows.time_reports'),
        permissions: {
          view: { fieldName: 'permissions.timeTrack.view' },
        },
      },
      {
        rowId: 'dashboard',
        rowTitle: t('barber:staff.permissions.rows.dashboard'),
        permissions: {
          view: { fieldName: 'permissions.dashboard.view' },
        },
      },
      {
        rowId: 'analytics',
        rowTitle: t('barber:staff.permissions.rows.analytics'),
        permissions: {
          view: { fieldName: 'permissions.analytics.view' },
        },
      },
    ],
  },
  {
    sectionId: 'marketingManagement',
    sectionTitle: t('barber:staff.permissions.sections.marketing_management'),
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'discounts',
        rowTitle: t('barber:staff.permissions.rows.discounts'),
        permissions: {
          view: { fieldName: 'permissions.discounts.view' },
          addEdit: { fieldName: 'permissions.discounts.modify' },
        },
      },
      {
        rowId: 'promos',
        rowTitle: t('barber:staff.permissions.rows.promos'),
        permissions: {
          view: { fieldName: 'permissions.promos.view' },
          addEdit: { fieldName: 'permissions.promos.modify' },
        },
      },
      {
        rowId: 'engage',
        rowTitle: t('barber:staff.permissions.rows.engage'),
        hide: !canSeeEngagePermissions,
        permissions: {
          view: { fieldName: 'permissions.engage.view' },
        },
      },
    ],
  },
  {
    sectionId: 'register',
    sectionTitle: t('barber:staff.permissions.sections.register'),
    showColumns: ['view'],
    rows: [
      {
        rowId: 'register',
        rowTitle: t('barber:staff.permissions.rows.register'),
        permissions: {
          view: { fieldName: 'permissions.register.view' },
        },
      },
      {
        rowId: 'cashRegister',
        rowTitle: t('barber:staff.permissions.rows.cash_register'),
        permissions: {
          view: { fieldName: 'permissions.cashRegister.view' },
        },
      },
      {
        rowId: 'openCashDrawer',
        rowTitle: t('barber:staff.permissions.rows.open_cash_drawer'),
        permissions: {
          view: { fieldName: 'permissions.cashRegister.modify' },
        },
      },
      {
        rowId: 'customCharge',
        rowTitle: t('barber:staff.permissions.rows.custom_charge'),
        permissions: {
          view: { fieldName: 'permissions.customCharge.view' },
        },
      },
      {
        rowId: 'dailySummary',
        rowTitle: t('barber:staff.permissions.rows.daily_summary'),
        permissions: {
          view: { fieldName: 'permissions.dailySummary.view' },
        },
      },
      {
        rowId: 'dailySummaryHistory',
        rowTitle: t('barber:staff.permissions.rows.daily_summary_history'),
        permissions: {
          view: { fieldName: 'permissions.dailySummaryHistory.view' },
        },
      },
    ],
  },
];
